
  import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
  import { Guid } from 'guid-typescript';
  import vSelect from 'vue-select';
  import {
    Client,
    IJobItemModel,
    JobItemModel,
    ILocationModel,
    LocationModel,
    ServiceTypeModel,
    EmployeeModel,
    JobItemUpdateScheduleModel
  } from '@/services/api.service';
  import { JobItemLocationModel } from './AddJobItemModel';
  import {
    JobItemType,
    Status,
    JobType,
    AreaType,
    EnumModel,
    StatusEnums,
    JobTypeEnums,
    JobItemTypeEnums,
    AreaTypeEnums
  } from '@/common/enumerations';
  import * as DateUtils from '@/utilities/date.utils';
  import * as StringUtils from '@/utilities/string.utils';
  import * as EnumUtils from '@/utilities/enum.utils';
  import { EventBus } from '@/event-bus';
  import { JobItemCreatedMessage } from '@/services/eventbus.models';

  @Component({
    components: {
      vSelect
    },
    props: {
      locations: {
        type: Array as () => ILocationModel[],
        required: true
      },
      jobGid: String,
      jobType: Number,
      jobIsQuoted: Boolean,
      isInDialogMode: Boolean,
      jobItemNumber: Number
    }
  })
  export default class AddJobItem extends Vue {
    // job item properties
    private itemName: string = '';
    private jobItemType: number = -1;
    private serviceTypeGid: string = '';
    private locationGid: string = '';
    private expectedStartDate: Date | null = null;
    private isFullDay: boolean = false;
    private purchaseOrderNumber: string = '';
    private status: number = 0;
    private taskContactName: string = '';
    private taskContactPhoneNumber: string = '';
    private quoteValue: number = 0;
    private isAdditionalToQuotedProjectScope: boolean = false;

    // private selectedLocation: JobItemLocationModel | null = null;
    private availableServiceTypes: ServiceTypeModel[] = [];
    private filteredServiceTypes: ServiceTypeModel[] = [];
    private availableJobItemTypes: EnumModel[] = [];
    private availableStatusTypes: EnumModel[] = [];
    private availableAreaTypes: EnumModel[] = [];
    // private availableLocations: LocationModel[] = [];
    private availableLocations: JobItemLocationModel[] = [];
    private locationsLoaded: boolean = false;

    private enableServiceTypeSelection: boolean = false;
    private enableEmployeeSelection: boolean = false;

    public scheduledDate: Date | null = null;
    public scheduledTimeRange: string = '';
    public estimatedDurationInMinutes: number = 0;
    public estimatedTravelTimeInMinutes: number = 0;

    public assignedEmployeeGid: string = '';
    public assignableEmployees: EmployeeModel[] = [];
    public assignableEmployeesPlaceholder: EmployeeModel | null = null;

    private mounted() {
      // get the networks
      // @ts-ignore
      this.$Progress.start();
      this.fetchInitialData();

      this.$props.locations.forEach((element: ILocationModel) => {
        const loc: JobItemLocationModel = new JobItemLocationModel();
        loc.location = element;
        loc.gid = element.gid!;
        if (element.address!.length > 0) {
          loc.name = element.address!;
        } else {
          loc.name = element.suburb! + ' ' + element.postcode!;
        }
        this.availableLocations.push(loc);
      });
      if (this.availableLocations.length > 0) {
        this.locationGid = this.availableLocations[0].gid;
      }
      this.locationsLoaded = true;

      const pickJobItemTypeModel: EnumModel = new EnumModel('(Select Job Item Type...)', -1);
      this.availableJobItemTypes.push(
        ...JobItemTypeEnums.sort((n1, n2) => StringUtils.stringCompare(n1.name || '', n2.name || ''))
      );
      this.availableJobItemTypes.unshift(pickJobItemTypeModel);
      // select the default job type based on the $props.jobType
      const matchedJIT = EnumUtils.findMatchingJobItemType(this.$props.jobType);
      if (matchedJIT > 0) {
        this.jobItemType = matchedJIT;
      }

      const pickStatusModel: EnumModel = new EnumModel('(Select Status...)', -1);
      this.availableStatusTypes.push(...StatusEnums);
      this.availableStatusTypes.unshift(pickStatusModel);

      const pickAreaTypeModel: EnumModel = new EnumModel('(Select Area...)', -1);
      this.availableAreaTypes.push(
        ...AreaTypeEnums.sort((n1, n2) => StringUtils.stringCompare(n1.name || '', n2.name || ''))
      );
      this.availableAreaTypes.unshift(pickAreaTypeModel);
    }

    private async fetchInitialData() {
      // Load all the initial data required for this process
      const clientService = new Client(process.env.VUE_APP_API_URL, this.$http);

      const [serviceTypes] = await Promise.all([clientService.getServiceTypes()]);

      // const pickServiceTypeModel: ServiceTypeModel = new ServiceTypeModel();
      // pickServiceTypeModel.init({ name: '(Select Service Type...)', gid: '' });
      this.availableServiceTypes = serviceTypes.sort((n1, n2) =>
        StringUtils.stringCompare(n1.name || '', n2.name || '')
      );
      // this.availableServiceTypes.unshift(pickServiceTypeModel);
      this.filterServiceTypes(this.jobItemType);
      this.updateAssignableEmployees(this.jobItemType);
      // @ts-ignore
      this.$Progress.finish();
    }

    private filterServiceTypes(selectedJobItemType: number) {
      // restrict the list of the job item types to
      // only those that match the selected
      if (selectedJobItemType === -1) {
        // the 'Select Job Item Type' option is selected
        this.filteredServiceTypes = [];
        const pickServiceTypeModel: ServiceTypeModel = new ServiceTypeModel();
        pickServiceTypeModel.init({ name: '(Select Job Item Type First...)', gid: '' });
        this.filteredServiceTypes.push(pickServiceTypeModel);
        // force set the selected item back to the picker item
        this.serviceTypeGid = pickServiceTypeModel.gid || '';
        this.enableServiceTypeSelection = false;
      } else {
        // filter and reload the list
        let theFilteredList: ServiceTypeModel[] = [];
        theFilteredList = this.availableServiceTypes.filter(
          st => st.parentJobType === 0 || st.parentJobType === selectedJobItemType
        );
        // Inject the selector item if there are
        // multiple items to show, otherwise default to the
        // first one
        if (theFilteredList.length !== 1) {
          const pickServiceTypeModel: ServiceTypeModel = new ServiceTypeModel();
          pickServiceTypeModel.init({ name: '(Select Service Type...)', gid: '' });
          theFilteredList.unshift(pickServiceTypeModel);
        }
        this.filteredServiceTypes = theFilteredList;

        this.enableServiceTypeSelection = true;
      }
    }

    private createJobItem() {
      const model: JobItemModel = new JobItemModel();
      const newGid = Guid.create();
      model.init({
        gid: newGid.toString(),
        jobGid: this.$props.jobGid,
        name: this.itemName,
        description: '',
        jobItemType: this.jobItemType,
        serviceTypeGid: this.serviceTypeGid || undefined,
        status: this.status,
        expectedStartDate: this.expectedStartDate,
        locationGid: this.locationGid || undefined,
        isFullDay: this.isFullDay,
        purchaseOrderNumber: this.purchaseOrderNumber,
        contactName: this.taskContactName,
        contactPhoneNumber: this.taskContactPhoneNumber,
        quoteValue: this.quoteValue,
        isAdditionalToQuotedProjectScope: this.isAdditionalToQuotedProjectScope,
      });

      const clientService: Client = new Client(process.env.VUE_APP_API_URL, this.$http);
      // @ts-ignore
      this.$Progress.start();
      clientService
        .createJobItem(this.$props.jobGid, model)
        .then(jobItemData => {
          // Schedule the item if the schedule is set
          // otherwise complete out the process
          if (this.scheduledDate != null) {
            this.createJobItemSchedule(jobItemData);
          } else {
            this.completeJobItemCreation(jobItemData);
          }
        })
        .catch(reason => {
          // @ts-ignore
          this.$Progress.fail();
          // @ts-ignore
          this.$awn.alert('Failed to create the job item.\r\n' + reason.message);
        });
    }

    private createJobItemSchedule(jobItem: IJobItemModel) {
      const clientService: Client = new Client(process.env.VUE_APP_API_URL, this.$http);
      const updateModel: JobItemUpdateScheduleModel = new JobItemUpdateScheduleModel();
      updateModel.jobItemScheduleGid = Guid.create().toString();
      updateModel.scheduledDateISO = DateUtils.toIsoDate(this.scheduledDate!);
      updateModel.scheduledTimeRange = this.scheduledTimeRange;
      updateModel.estimatedDurationInMinutes = this.estimatedDurationInMinutes;
      updateModel.estimatedTravelTimeInMinutes = this.estimatedTravelTimeInMinutes;
      updateModel.assignedToEmployeeGid = this.assignedEmployeeGid || undefined;
      // updateModel.notes

      clientService.scheduleJobItem(jobItem.jobGid!, jobItem.gid!, updateModel).then(scheduleData => {
        this.completeJobItemCreation(jobItem);
      });
    }

    private completeJobItemCreation(jobItem: IJobItemModel) {
      // @ts-ignore
      this.$Progress.finish();
      // @ts-ignore
      this.$awn.success('Job item ' + this.itemName + ' created successfully');
      // Raise the created events, and publish the event on the bus
      this.$emit('job-item-created', jobItem);
      EventBus.$emit('job-item-created', new JobItemCreatedMessage(this.$props.jobGid, jobItem));
      this.closeModal();
    }

    private cancelEdit() {
      console.log('Cancelling edit...');
      // this.$emit('close');
      this.closeModal();
    }

    private closeModal() {
      if (this.$props.isInDialogMode) {
        this.$modal.hide('modal-jobitem-create');
      }
    }

    // if status is pending and schedule value has been entered, switch status to  'scheduled'
    @Watch('scheduledDate')
    private onScheduledDateChanged(value: Date | null, oldValue: Date | null) {
      if (value != null && oldValue == null && this.status === 0) {
        this.status = 1;
      }
    }

    // if the item name has not been manually set, generate a name based on the selected serviceType
    @Watch('serviceTypeGid')
    private onServiceTypeGidChanged(value: string, oldValue: string) {
      const serviceType = this.availableServiceTypes.find(t => t.gid === value);

      if (value == null || serviceType == null) {
        return;
      }

      if (oldValue === '') {
        this.itemName = serviceType.name!;
      } else {
        const oldServiceType = this.availableServiceTypes.find(t => t.gid === oldValue);

        if (oldServiceType === null) {
          this.itemName = serviceType.name!;
        } else if (this.itemName === oldServiceType!.name) {
          this.itemName = serviceType.name!;
        }
      }
    }

    @Watch('jobItemType')
    private onjobTypeChanged(value: number, oldValue: number) {
      this.filterServiceTypes(value);
      this.updateAssignableEmployees(value);
    }

    private updateAssignableEmployees(selectedJobItemType: number) {
      if (selectedJobItemType === -1) {
        // the 'Select Job Item Type' option is selected
        // this.assignableEmployees = [];
        const assignableEmployeesPlaceholder = new EmployeeModel();
        assignableEmployeesPlaceholder.init({ firstName: '(Select Job Item Type First...)', lastName: '', gid: '' });
        this.assignableEmployees.push(assignableEmployeesPlaceholder);
        // force set the selected item back to the picker item
        this.assignedEmployeeGid = assignableEmployeesPlaceholder.gid || '';
        this.enableEmployeeSelection = false;
      } else {
        const clientService: Client = new Client(process.env.VUE_APP_API_URL, this.$http);

        const employees = clientService.getAssignableEmployees(selectedJobItemType as any).then(data => {
          const sortedData = data.sort((n1, n2) => StringUtils.stringCompare(n1.firstName || '', n2.firstName || ''));
          const assignableEmployeesPlaceholder = new EmployeeModel();
          assignableEmployeesPlaceholder.init({ firstName: '(Select Employee...)', lastName: '', gid: '' });
          sortedData.unshift(assignableEmployeesPlaceholder);

          this.assignableEmployees = sortedData;
          this.assignedEmployeeGid = assignableEmployeesPlaceholder.gid || '';
          this.enableEmployeeSelection = true;
        });
      }
    }
  }
